import type { Asset, DividendEvent, FireConfig, UserProfile } from '../types';

export type FireSetupStepId =
    | 'create-account'
    | 'display-nickname'
    | 'fire-target'
    | 'net-worth-assets'
    | 'pension-details'
    | 'portfolio-holdings'
    | 'dividend-income';

export type FireSetupActionTarget = 'public-profile' | 'fire' | 'pensions' | 'portfolio';

export interface FireSetupStep {
    id: FireSetupStepId;
    label: string;
    completed: boolean;
    actionLabel?: string;
    actionTarget?: FireSetupActionTarget;
}

export interface FireSetupCompletionInput {
    user?: Partial<UserProfile> | null;
    accountSettings?: {
        displayName?: string;
        displayNickname?: string;
    } | null;
    fireConfig?: Partial<FireConfig> | null;
    currentNetWorth?: number | null;
    holdings?: Array<Partial<Asset>> | null;
    totalDividendsReceived?: number | null;
    dividendForecastEvents?: Array<Partial<DividendEvent>> | null;
    legacyOnboardingComplete?: boolean;
}

export interface FireSetupCompletionResult {
    steps: FireSetupStep[];
    completedCount: number;
    totalCount: number;
    percent: number;
    isComplete: boolean;
}

const hasPositiveNumber = (value: unknown) => typeof value === 'number' && Number.isFinite(value) && value > 0;

const hasText = (value: unknown) => typeof value === 'string' && value.trim().length > 0;

const isPassiveIncomeType = (type: unknown) => {
    const normalized = String(type || '').toLowerCase();
    return normalized === 'rental' || normalized === 'pension' || normalized === 'annuity' || normalized === 'other';
};

export const calculateFireSetupCompletion = ({
    user,
    accountSettings,
    fireConfig,
    currentNetWorth,
    holdings,
    totalDividendsReceived,
    dividendForecastEvents,
    legacyOnboardingComplete = false,
}: FireSetupCompletionInput): FireSetupCompletionResult => {
    const manualAssets = Array.isArray(fireConfig?.manualAssets) ? fireConfig.manualAssets : [];
    const pensionIncome = Array.isArray(fireConfig?.pensions) ? fireConfig.pensions : [];
    const incomeStreams = Array.isArray(fireConfig?.incomeStreams) ? fireConfig.incomeStreams : [];
    const activeHoldings = Array.isArray(holdings) ? holdings : [];
    const upcomingDividendEvents = Array.isArray(dividendForecastEvents) ? dividendForecastEvents : [];

    const hasFireTargetSettings =
        hasPositiveNumber(fireConfig?.retirementAge) &&
        Number(fireConfig?.retirementAge) > Number(fireConfig?.currentAge || 0) &&
        (hasPositiveNumber(fireConfig?.annualExpenses) || hasPositiveNumber(fireConfig?.retirementExpenses)) &&
        hasPositiveNumber(fireConfig?.withdrawalRate);

    const hasNetWorthAssets = manualAssets.some((asset) => hasPositiveNumber(asset.value)) || hasPositiveNumber(currentNetWorth);

    const hasPensionDetails =
        manualAssets.some((asset) => asset.type === 'Pension' && hasPositiveNumber(asset.value)) ||
        pensionIncome.some((pension) => hasPositiveNumber(pension.annualAmount)) ||
        incomeStreams.some((stream) => String(stream.type || '').toLowerCase() === 'pension' && hasPositiveNumber(stream.amount));

    const hasPortfolioHoldings = activeHoldings.some((holding) => {
        const symbol = String(holding.symbol || '').toUpperCase();
        return symbol !== 'CASH' && hasPositiveNumber(holding.quantity);
    });

    const hasDividendIncome =
        hasPositiveNumber(totalDividendsReceived) ||
        upcomingDividendEvents.length > 0 ||
        incomeStreams.some((stream) => isPassiveIncomeType(stream.type) && hasPositiveNumber(stream.amount));

    const hasDisplayNickname = hasText(accountSettings?.displayNickname) || hasText(accountSettings?.displayName) || hasText(user?.displayName);

    const calculatedSteps: FireSetupStep[] = [
        { id: 'create-account', label: 'Create account', completed: Boolean(user) },
        {
            id: 'display-nickname',
            label: 'Set display nickname',
            completed: hasDisplayNickname,
            actionLabel: 'Edit profile',
            actionTarget: 'public-profile',
        },
        {
            id: 'fire-target',
            label: 'Add FIRE target settings',
            completed: hasFireTargetSettings,
            actionLabel: 'Open FIRE Lab',
            actionTarget: 'fire',
        },
        {
            id: 'net-worth-assets',
            label: 'Add net worth / assets',
            completed: hasNetWorthAssets,
            actionLabel: 'Update assets',
            actionTarget: 'fire',
        },
        {
            id: 'pension-details',
            label: 'Add pension details',
            completed: hasPensionDetails,
            actionLabel: 'Open pensions',
            actionTarget: 'pensions',
        },
        {
            id: 'portfolio-holdings',
            label: 'Add portfolio holdings',
            completed: hasPortfolioHoldings,
            actionLabel: 'Open portfolio',
            actionTarget: 'portfolio',
        },
        {
            id: 'dividend-income',
            label: 'Add dividend/income data',
            completed: hasDividendIncome,
            actionLabel: 'Update income data',
            actionTarget: 'portfolio',
        },
    ];

    const steps = legacyOnboardingComplete ? calculatedSteps.map((step) => ({ ...step, completed: true })) : calculatedSteps;
    const completedCount = steps.filter((step) => step.completed).length;
    const totalCount = steps.length;
    const percent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    return {
        steps,
        completedCount,
        totalCount,
        percent,
        isComplete: completedCount === totalCount,
    };
};
